import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Phone, ChevronDown, ChevronUp, Users } from 'lucide-react';
import { useFetchDocuments } from '../../hooks/useFetchDocuments';
import { Card, Skeleton } from '../ui';

function UserRow({ user }) {
  const [expanded, setExpanded] = useState(false);
  const initials = (user.name || '?').split(' ').slice(0, 2).map((n) => n[0]).join('').toUpperCase();

  return (
    <div className="border-b border-border last:border-0">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-bg-secondary transition-colors duration-normal focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-border-focus"
      >
        <span className="w-8 h-8 rounded-full bg-primary-subtle flex items-center justify-center text-xs font-bold text-primary shrink-0">
          {initials}
        </span>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-text-primary truncate">{user.name}</p>
          <p className="text-xs text-text-muted truncate">{user.cellphone}</p>
        </div>
        {expanded ? <ChevronUp size={16} className="shrink-0 text-text-muted" /> : <ChevronDown size={16} className="shrink-0 text-text-muted" />}
      </button>

      {expanded && (
        <div className="px-4 pb-4 pt-2 bg-bg-secondary flex flex-col gap-1.5">
          <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
            <div><dt className="text-xs text-text-muted">Nome</dt><dd className="font-medium text-text-primary">{user.name}</dd></div>
            <div>
              <dt className="text-xs text-text-muted">Celular</dt>
              <dd>
                <Link
                  to={`https://api.whatsapp.com/send?phone=${(user.cellphone || '').replace(/\(|\)|-| /g, '')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-primary hover:underline flex items-center gap-1"
                >
                  <Phone size={12} aria-hidden="true" /> {user.cellphone}
                </Link>
              </dd>
            </div>
            {user.createdAt?.[0] && (
              <div><dt className="text-xs text-text-muted">Cadastrado</dt><dd className="text-sm text-text-primary">{user.createdAt[0]}</dd></div>
            )}
            <div className="col-span-2"><dt className="text-xs text-text-muted">ID</dt><dd className="font-mono text-xs text-text-muted break-all">{user.id}</dd></div>
          </dl>
        </div>
      )}
    </div>
  );
}

export default function UsersList() {
  const [search, setSearch] = useState('');
  const { documents, loading, error } = useFetchDocuments('users');

  const filtered = (documents || []).filter((u) =>
    !search || u.name?.toLowerCase().includes(search.toLowerCase()) || u.cellphone?.includes(search),
  );

  return (
    <div className="flex flex-col gap-4">
      {/* Busca */}
      <Card>
        <Card.Body className="py-3">
          <div className="flex flex-col gap-1">
            <label htmlFor="user-search" className="text-xs font-medium text-text-muted">Buscar usuário</label>
            <input
              id="user-search"
              type="text"
              placeholder="Nome ou celular..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="rounded-md border border-border px-3 py-2 text-sm bg-surface text-text-primary focus:outline-none focus:ring-2 focus:ring-border-focus placeholder:text-text-muted"
            />
          </div>
        </Card.Body>
      </Card>

      {/* Lista */}
      <Card>
        <Card.Header>
          <h2 className="text-sm font-semibold text-text-primary">
            Usuários cadastrados{' '}
            {!loading && <span className="text-text-muted font-normal">({filtered.length})</span>}
          </h2>
        </Card.Header>

        {loading ? (
          <Card.Body className="flex flex-col gap-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-3">
                <Skeleton className="w-8 h-8 rounded-full" />
                <div className="flex flex-col gap-1.5 flex-1">
                  <Skeleton className="h-3.5 w-2/5" />
                  <Skeleton className="h-3 w-1/4" />
                </div>
              </div>
            ))}
          </Card.Body>
        ) : error ? (
          <Card.Body><p className="text-sm text-danger-text">Erro ao carregar usuários.</p></Card.Body>
        ) : filtered.length === 0 ? (
          <Card.Body>
            <div className="flex flex-col items-center gap-3 py-8 text-center">
              <Users size={28} className="text-text-muted" aria-hidden="true" />
              <p className="text-sm text-text-muted">Nenhum usuário encontrado.</p>
            </div>
          </Card.Body>
        ) : (
          <div>
            {filtered.map((user) => <UserRow key={user.id} user={user} />)}
          </div>
        )}
      </Card>
    </div>
  );
}
